import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeftIcon,
  ArrowDownTrayIcon,
  PrinterIcon,
} from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { threatIntelligenceService } from '@/services/threat-intelligence.service';
import toast from 'react-hot-toast';
import { sanitizeHtml } from '@/utils/sanitize';

export default function ReportPreviewPage() {
  const { reportId } = useParams<{ reportId: string }>();
  const navigate = useNavigate();
  const [htmlContent, setHtmlContent] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [reportData, setReportData] = useState<any>(null); // Keep as any for now or define report interface, assuming partial ThreatReport
  const hasLoadedRef = useRef(false);

  const loadReport = useCallback(async () => {
    if (!reportId) { return; }

    try {
      const data = await threatIntelligenceService.getReport(reportId);
      if (data) {
        setReportData(data);
      }
    } catch (err) {
      console.error('Failed to load report metadata:', err);
    }
  }, [reportId]);

  const loadPreview = useCallback(async () => {
    if (!reportId) { return; }

    setIsLoading(true);
    setError(null);

    try {
      const html = await threatIntelligenceService.getReportPreview(reportId);
      if (html) {
        setHtmlContent(html);
      } else {
        setError('Failed to load report preview');
      }
    } catch (err) {
      const error = err as { response?: { status?: number }; status?: number; message?: string };
      // Handle rate limiting with a helpful message
      if (error?.response?.status === 429 || error?.status === 429) {
        setError('Too many requests. Please wait a moment and try again.');
      } else {
        setError(error?.message ?? 'Failed to load preview');
      }
    } finally {
      setIsLoading(false);
    }
  }, [reportId]);

  useEffect(() => {
    // Prevent duplicate loads (especially in React Strict Mode)
    if (reportId && !hasLoadedRef.current) {
      hasLoadedRef.current = true;
      loadReport();
      loadPreview();
    }

    // Reset flag when reportId changes
    return () => {
      if (hasLoadedRef.current) {
        hasLoadedRef.current = false;
      }
    };
  }, [reportId, loadReport, loadPreview]);

  const handleDownload = async () => {
    if (!reportId || !reportData) { return; }

    setIsDownloading(true);
    try {
      const fileExtension = reportData.format === 'excel' ? 'xlsx' : reportData.format;
      const filename = `${reportData.reportNumber ?? 'report'}.${fileExtension}`;
      const success = await threatIntelligenceService.downloadReport(
        reportId,
        filename
      );
      if (success) {
        toast.success('Report downloaded successfully');
      } else {
        toast.error('Failed to download report');
      }
    } catch (err) {
      toast.error((err as Error).message || 'Failed to download');
    } finally {
      setIsDownloading(false);
    }
  };

  const handlePrint = () => {
    if (!htmlContent) { return; }

    // Create a new window with the HTML content for printing
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
      }, 250);
    }
  };

  const handleBack = () => {
    navigate('/security/threats');
  };

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4, ease: 'easeOut' },
    },
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="min-h-screen bg-secondary-50 dark:bg-secondary-900"
    >
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white dark:bg-secondary-800 border-b border-secondary-200 dark:border-secondary-700 shadow-sm">
        <div className="px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                onClick={handleBack}
                variant="ghost"
                size="sm"
                className="text-secondary-600 hover:text-secondary-900 dark:text-secondary-400 dark:hover:text-white"
              >
                <ArrowLeftIcon className="h-5 w-5 mr-2" />
                Back to Reports
              </Button>
              <div className="hidden sm:block border-l border-secondary-300 dark:border-secondary-600 h-6" />
              <div className="hidden sm:block">
                <h1 className="text-xl font-semibold text-secondary-900 dark:text-white">
                  Report Preview
                </h1>
                {reportData && (
                  <p className="text-sm text-secondary-600 dark:text-secondary-400">
                    {reportData.reportNumber} • {reportData.title}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Button
                onClick={handlePrint}
                disabled={isLoading || !htmlContent}
                variant="outline"
                size="sm"
                className="hidden md:flex"
              >
                <PrinterIcon className="h-4 w-4 mr-2" />
                Print
              </Button>
              <Button
                onClick={handleDownload}
                disabled={isDownloading || isLoading}
                size="sm"
                className="bg-primary-600 hover:bg-primary-700 text-white"
              >
                {isDownloading ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Downloading...
                  </>
                ) : (
                  <>
                    <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                    Download
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Mobile title */}
          <div className="sm:hidden mt-3">
            <h1 className="text-lg font-semibold text-secondary-900 dark:text-white">
              Report Preview
            </h1>
            {reportData && (
              <p className="text-xs text-secondary-600 dark:text-secondary-400 mt-1">
                {reportData.reportNumber}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 sm:px-6 lg:px-8 py-6">
        {isLoading && (
          <Card>
            <CardContent className="p-12 text-center">
              <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin mx-auto mb-4" />
              <p className="text-secondary-600 dark:text-secondary-400">Loading preview...</p>
            </CardContent>
          </Card>
        )}

        {error && (
          <Card>
            <CardContent className="p-12 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-danger-100 dark:bg-danger-900/20 mb-4">
                <svg
                  className="h-8 w-8 text-danger-600 dark:text-danger-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-secondary-900 dark:text-white mb-2">
                Preview Error
              </h3>
              <p className="text-secondary-600 dark:text-secondary-400 mb-4">{error}</p>
              <Button onClick={loadPreview} variant="outline">
                Try Again
              </Button>
            </CardContent>
          </Card>
        )}

        {!isLoading && !error && htmlContent && (
          <Card className="shadow-lg">
            <CardContent className="p-0">
              {/* Render HTML content */}
              <div
                className="prose prose-sm sm:prose lg:prose-lg !max-w-none p-6 sm:p-8 lg:p-12"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(htmlContent) }}
              />
            </CardContent>
          </Card>
        )}

        {!isLoading && !error && !htmlContent && (
          <Card>
            <CardContent className="p-12 text-center">
              <p className="text-secondary-600 dark:text-secondary-400">No preview available</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Mobile footer actions */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white dark:bg-secondary-800 border-t border-secondary-200 dark:border-secondary-700 px-4 py-3 shadow-lg">
        <div className="flex space-x-2">
          <Button
            onClick={handlePrint}
            disabled={isLoading || !htmlContent}
            variant="outline"
            className="flex-1"
          >
            <PrinterIcon className="h-4 w-4 mr-2" />
            Print
          </Button>
          <Button onClick={handleBack} variant="outline" className="flex-1">
            Close
          </Button>
        </div>
      </div>
    </motion.div>
  );
}
